import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  FilterType,
  Flex,
  Table,
  TableFilterV2 as TableFilter,
  useTable,
  useFilters,
  RoleGuard,
} from 'portal-commons';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { faPlus, faArrowDownToLine } from '@fortawesome/pro-solid-svg-icons';

import {
  CampaignBuilderResult,
  campaignController,
  CspCampaign,
  enumController,
  UseCase,
} from '@/apis/core';
import {
  getCnpListOptions,
  getBrandNameAutocompleteOptions,
} from '@/apis/services';
import { getBadgeTypeByStatus } from '@/applications/CspPortal/Brands/utils';
import { PageContainer, PageTitleV2 as PageTitle } from '@/components';
import { Loader } from '@/shared_elements';
import {
  CLEAR_LOADING_MESSAGE,
  SET_LOADING_MESSAGE,
} from '@/shared_elements/actions';
import { convertTimeWithTimezone } from '@/utils/time';
import { toastFlashMessage } from '@/utils';
import { generateAndDownloadFile } from '@/utils/download';
import { humanize } from '@/utils/string';
import { MY_CAMPAIGNS_HU_PATH } from '@/constants/paths';
import { remapTCRStatus } from '../utils';
import { RegistrationModal } from '../components';
import { SelectItem } from '@/utils/types';

const FilterConfigs = {
  campaignUid: {
    type: FilterType.Search,
    label: 'Program ID',
    placeholder: 'Enter Program ID',
  },
  brandUid: {
    type: FilterType.Search,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  brandName: {
    type: FilterType.Search,
    label: 'Brand DBA Name',
    placeholder: 'Enter Brand DBA Name',
    suggestions: [],
  },
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use Case',
  },
  upstreamCnpUid: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Upstream CNP',
  },
  dcaCompletion: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
    label: 'DCA Complete',
  },
  status: {
    type: FilterType.Dropdown,
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    label: 'TCR Status',
  },
};

const FilterLoaders = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) =>
        getBrandNameAutocompleteOptions(value, {
          status: 'ACTIVE',
        }),
    },
  },
};

const TableHeaders = [
  { id: 'uid', label: 'PROGRAM ID', sortable: true },
  { id: 'brandUid', label: 'BRAND ID', sortable: true },
  { id: 'brandName', label: 'BRAND DBA NAME', sortable: false },
  { id: 'usecase', label: 'USE CASE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'upstreamCnpName', label: 'UPSTREAM CNP', sortable: false },
  { id: 'dcaCompletion', label: 'DCA COMPLETE', sortable: false },
  { id: 'status', label: 'TCR STATUS', sortable: true },
];

const MAX_DISPLAY_RECORDS = 10000;

const MyCampaignsHU: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [registrationModal, setRegistrationModal] = useState(false);
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [cnpOptions, setCnpOptions] = useState<SelectItem[]>([]);

  const {
    configs: filterConfigs,
    appliedFilters,
    candidateFilters,
    additionalFilters,
    setOptions,
    handleEdit,
    handleApply,
  } = useFilters({
    configs: FilterConfigs,
    loaders: FilterLoaders,
  });

  const getQueries = (tableFilters: Record<string, unknown> = {}) => {
    return {
      ...tableFilters,
      ...appliedFilters,
      ...additionalFilters,
      provisional: false,
      channelId: 'SMS',
    };
  };

  const {
    pageLoading,
    loading: tableLoading,
    filter: tableFilters,
    tableData: campaigns,
    pagination,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    total: totalRecords,
  } = useTable<CspCampaign>(
    async (params) => {
      const response = await campaignController.getCampaigns(
        getQueries(params)
      );
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      cappingTotal: MAX_DISPLAY_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const fetchFilterOptions = async () => {
    const [ucs, cnps] = await Promise.all([
      enumController.getUseCases({ channelId: 'SMS' }),
      getCnpListOptions({ channelId: 'SMS' }),
    ]);

    const usecaseOptions = ucs.map((useCase) => ({
      label: useCase.displayName,
      value: useCase.id,
    }));

    setOptions('usecase', usecaseOptions);
    setOptions('upstreamCnpUid', cnps);

    setUseCases(ucs);
    setCnpOptions(cnps);
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const formatter = {
    wrap: (value: string, testId: string) =>
      value?.length > 0 ? (
        <Box
          sx={{
            maxWidth: '350px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          data-testid={testId}
        >
          {value}
        </Box>
      ) : null,
    fallbackTo: (
      value: React.ReactNode,
      testId: string,
      fallbackValue = 'N/A'
    ) => value || <span data-testid={testId}>{fallbackValue}</span>,
    renderStatus: (status: string | null | undefined, testId: string) => {
      if (!status) return <span data-testid={testId}>N/A</span>;
      const type = getBadgeTypeByStatus(status);
      return (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={type}
          data-testid={testId}
        >
          {status ? humanize(remapTCRStatus(status)) : 'N/A'}
        </Badge>
      );
    },
  };

  const tableData = useMemo(() => {
    return campaigns.map((campaign) => ({
      rawUid: campaign.uid,
      uid: <span data-testid="uid">{campaign.uid}</span>,
      brandUid: formatter.fallbackTo(
        formatter.wrap(campaign.brandUid || '', 'brandUid'),
        'brandUid'
      ),
      brandName: formatter.fallbackTo(
        formatter.wrap(campaign.brandName || '', 'brandName'),
        'brandName'
      ),
      usecase: formatter.fallbackTo(
        campaign.usecase ? (
          <span data-testid="usecase">
            {useCases.find((uc) => uc.id === campaign.usecase)?.displayName ||
              campaign.usecase}
          </span>
        ) : null,
        'usecase'
      ),
      createDate: (
        <span data-testid="createDate">
          {convertTimeWithTimezone(
            campaign.createDate,
            undefined,
            'MM/DD/YYYY'
          )}
        </span>
      ),
      upstreamCnpName: formatter.fallbackTo(
        formatter.wrap(campaign.upstreamCnpName || '', 'upstreamCnpName'),
        'upstreamCnpName'
      ),
      dcaCompletion: (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={campaign.dcaCompletion ? 'success' : 'neutral'}
          data-testid="dcaCompletion"
        >
          {campaign.dcaCompletion ? 'Yes' : 'No'}
        </Badge>
      ),
      status: formatter.renderStatus(campaign.status, 'status'),
    }));
  }, [campaigns, useCases]);

  const handleCampaignAdd = () => {
    // history.push(`${MY_CAMPAIGNS_HU_PATH}/create`);
    setRegistrationModal(true);
  };

  const handleRowClick = (data: { rawUid?: string | number }) => {
    history.push({
      pathname: `${MY_CAMPAIGNS_HU_PATH}/${data.rawUid}`,
      state: {
        goBackPage: `${location.pathname}${
          location.search ? location.search : ''
        }`,
      },
    });
  };

  const handleDownload = async () => {
    dispatch({
      type: SET_LOADING_MESSAGE,
      payload: 'We are generating the document...',
    });
    const response = await campaignController.downloadCspCampaigns(
      getQueries(tableFilters)
    );

    if (response) {
      const file = new File([response], 'my-messaging-programs.csv');
      generateAndDownloadFile(file);
    } else {
      toastFlashMessage('Failed to download messaging programs', 'error');
    }
    dispatch({ type: CLEAR_LOADING_MESSAGE });
  };

  const handleSucceed = (data: CampaignBuilderResult) => {
    setRegistrationModal(false);
    toastFlashMessage('Messaging program successfully registered', 'success');
    history.push({
      pathname: `${MY_CAMPAIGNS_HU_PATH}/${data.campaignUid}`,
    });
  };

  if (pageLoading) return <Loader hocFlag />;

  return (
    <PageContainer
      sx={{
        paddingX: '96px',
        rowGap: null,
        '& .MuiAutocomplete-root': {
          width: 'unset',
        },
      }}
    >
      <Flex sx={{ justifyContent: 'space-between', width: '100%' }}>
        <PageTitle
          note="Results capped at first 10,000 records"
          notePlacement="center"
        >
          {totalRecords} Messaging Programs
        </PageTitle>
        <RoleGuard feature="campaignList.addCampaign">
          <Button
            type="button"
            size="lg"
            shape="square"
            variant="fill"
            color="primary"
            startAdornment={faPlus}
            onClick={handleCampaignAdd}
            data-testid="addProgram"
          >
            Message Program
          </Button>
        </RoleGuard>
      </Flex>

      <Flex
        sx={{
          width: '100%',
          marginTop: '16px',
          marginBottom: '16px',
        }}
      >
        <Button
          type="button"
          size="lg"
          shape="square"
          variant="outline"
          color="primary"
          startAdornment={faArrowDownToLine}
          onClick={handleDownload}
          disabled={!totalRecords}
          data-testid="downloadPrograms"
        >
          Download
        </Button>
      </Flex>

      <Box sx={{ width: '100%', mb: 'm' }}>
        <TableFilter
          configs={filterConfigs}
          candidateValues={candidateFilters}
          appliedValues={appliedFilters}
          onCandidateValuesChange={handleEdit}
          onAppliedValuesChange={handleApply}
          data-testid="programsTableFilter"
        />
      </Box>

      <Table
        onRowClick={handleRowClick}
        loading={tableLoading}
        headRows={TableHeaders}
        emptyState="no programs to view"
        data={tableData}
        rowKey="rawUid"
        handleChangePage={handlePageChange}
        createSortHandler={handleSortingChange}
        filter={tableFilters}
        pagination={pagination}
        emptyBlockHeight="180px"
        testId="programsTable"
      />

      {registrationModal && (
        <RegistrationModal
          onCancel={() => setRegistrationModal(false)}
          onSucceed={handleSucceed}
          useCases={useCases}
          cnpOptions={cnpOptions}
        />
      )}
    </PageContainer>
  );
};

export default MyCampaignsHU;
